import React from 'react'
import { Alert } from 'antd'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import { dataTypeLabel, type SurecoCsvDataType } from '../constants'

interface Props {
  phase: 'detecting' | 'columnErrors' | 'noMatch'
  detectedDataType: SurecoCsvDataType | null
  columnErrors: string[]
}

// Shared body content for the three non-idle, non-feature-specific phases of
// CSV data-type detection (Health Plan and Compare Data both render this
// unchanged; what happens on a confident match is each feature's own).
export function CsvDetectionPhaseContent({
  phase,
  detectedDataType,
  columnErrors,
}: Props) {
  if (phase === 'detecting') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          padding: '40px 16px',
        }}
      >
        <LoadingOutlined style={{ fontSize: 24, color: '#1677ff' }} />
        <span style={{ fontWeight: 500, marginTop: 8 }}>
          Processing your request
        </span>
        <span style={{ fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
          Please wait while we process your request. Do not refresh the page.
        </span>
      </div>
    )
  }

  if (phase === 'columnErrors' && detectedDataType) {
    return (
      <Alert
        type="error"
        showIcon
        message="Upload failed"
        description={
          <>
            The system detected this file is missing one or more columns
            required for <strong>{dataTypeLabel(detectedDataType)}</strong>.
            Please add the missing columns and upload the file again.
            <div style={{ fontWeight: 500, marginTop: 8 }}>
              Missing column{columnErrors.length > 1 ? 's' : ''}:
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 4 }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {columnErrors.map((column) => (
                  <li key={column}>{column}</li>
                ))}
              </ul>
            </div>
          </>
        }
      />
    )
  }

  if (phase === 'noMatch') {
    return (
      <Alert
        type="error"
        showIcon
        message="Upload failed"
        description="The system could not identify a matching data type for this file. Please check the file's columns and upload it again."
      />
    )
  }

  return null
}
